package com.nonopn.skydome.common.app.home

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.DropdownMenu
import androidx.compose.material.DropdownMenuItem
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.Home
import androidx.compose.material.icons.filled.Star
import com.nonopn.icons.MapLocation
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

import com.nonopn.icons.FixedGps
import com.nonopn.icons.Folder
import com.nonopn.icons.ManageAccounts
import com.nonopn.skydome.common.app.drawer.DrawerItem
import com.nonopn.skydome.common.app.drawer.DrawerSeparator
import com.nonopn.skydome.common.app.drawer.DrawerTitle
import com.nonopn.skydome.common.app.navigation.RouterActivity
import com.nonopn.skydome.common.app.navigation.RouterDashboard
import com.nonopn.skydome.common.app.navigation.RouterDocuments
import com.nonopn.skydome.common.app.navigation.RouterEstimatesAdmin
import com.nonopn.skydome.common.app.navigation.RouterMapAdmin
import com.nonopn.skydome.common.app.navigation.RouterPromotionCodesAdmin
import com.nonopn.skydome.common.app.navigation.RouterProbes
import com.nonopn.skydome.common.app.navigation.RouterProjects
import com.nonopn.skydome.common.app.navigation.RouterSettings
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.menu_admin_estimate_list
import com.nonopn.skywatcher.common.app.res.menu_admin_mode_enter
import com.nonopn.skywatcher.common.app.res.menu_admin_mode_exit
import com.nonopn.skywatcher.common.app.res.menu_admin_mode_title
import com.nonopn.skywatcher.common.app.res.menu_admin_promo_codes
import com.nonopn.skywatcher.common.app.res.menu_sr_account
import com.nonopn.skywatcher.common.app.res.menu_sr_activity
import com.nonopn.skywatcher.common.app.res.menu_sr_dashboard
import com.nonopn.skywatcher.common.app.res.menu_sr_documents
import com.nonopn.skywatcher.common.app.res.menu_sr_probes
import com.nonopn.skywatcher.common.app.res.menu_sr_projects
import com.nonopn.skywatcher.design.theme.AppColor
import com.nonopn.skywatcher.design.utils.localized

import de.drick.compose.hotpreview.HotPreview
import eu.codlab.compose.widgets.TextNormal
import eu.codlab.compose.widgets.spacers.BottomSpacer
import eu.codlab.compose.widgets.spacers.TopSpacer
import eu.codlab.navigation.NavigateTo
import eu.codlab.navigation.RouteParameterTo
import eu.codlab.navigation.RouterNoParameters

@Suppress("LongMethod")
@Composable
fun DrawerContentNormal(
    modifier: Modifier = Modifier,
    currentRoute: RouteParameterTo? = null,
    tiny: Boolean = false,
    onMenuItemSelected: (title: String, navigateTo: NavigateTo) -> Unit
) {
    val appModel = LocalApp.current
    val state by appModel.states.collectAsState()
    val hasApi = state.apiProvider?.apiNormal != null

    // Administrator mode: dedicated menu (dashboard, keraunic map, probes) + way back to the user mode
    if (state.isAdminMode && state.apiProvider?.apiAdmin != null) {
        DrawerContentAdmin(
            modifier = modifier,
            currentRoute = currentRoute,
            tiny = tiny,
            onMenuItemSelected = onMenuItemSelected
        )
        return
    }

    Column(
        modifier = modifier
            .fillMaxSize()
            .background(color = AppColor.StrikeSidebar)
            .verticalScroll(rememberScrollState())
    ) {
        TopSpacer()

        DrawerMenuEntry(
            text = Res.string.menu_sr_dashboard.localized(),
            currentRoute = currentRoute,
            router = RouterDashboard,
            image = Icons.Filled.Home,
            tiny = tiny,
            onMenuItemSelected = onMenuItemSelected
        )

        if (hasApi) {
            DrawerSeparator(Modifier.padding(vertical = 8.dp))

            DrawerMenuEntry(
                text = Res.string.menu_sr_activity.localized(),
                currentRoute = currentRoute,
                router = RouterActivity,
                image = Icons.Rounded.MapLocation,
                tiny = tiny,
                onMenuItemSelected = onMenuItemSelected
            )

            DrawerSeparator(Modifier.padding(vertical = 8.dp))

            DrawerMenuEntry(
                text = Res.string.menu_sr_projects.localized(),
                currentRoute = currentRoute,
                router = RouterProjects,
                image = Icons.Rounded.Folder,
                tiny = tiny,
                onMenuItemSelected = onMenuItemSelected
            )

            DrawerSeparator(Modifier.padding(vertical = 8.dp))

            DrawerMenuEntry(
                text = Res.string.menu_sr_documents.localized(),
                currentRoute = currentRoute,
                router = RouterDocuments,
                image = Icons.Rounded.Folder,
                tiny = tiny,
                onMenuItemSelected = onMenuItemSelected
            )

            DrawerSeparator(Modifier.padding(vertical = 8.dp))

            DrawerMenuEntry(
                text = Res.string.menu_sr_probes.localized(),
                currentRoute = currentRoute,
                router = RouterProbes,
                image = Icons.Rounded.FixedGps,
                tiny = tiny,
                onMenuItemSelected = onMenuItemSelected
            )
        }

        DrawerSeparator(Modifier.padding(vertical = 8.dp))

        DrawerMenuEntry(
            text = Res.string.menu_sr_account.localized(),
            currentRoute = currentRoute,
            router = RouterSettings,
            image = Icons.Outlined.ManageAccounts,
            tiny = tiny,
            onMenuItemSelected = onMenuItemSelected
        )

        DrawerSeparator(Modifier.padding(vertical = 8.dp))

        // Admin accounts only: switch to the administrator mode
        if (state.apiProvider?.apiAdmin != null) {
            DrawerSeparator(Modifier.padding(vertical = 8.dp))

            DrawerItem(
                text = Res.string.menu_admin_mode_enter.localized(),
                router = null,
                image = Icons.Outlined.ManageAccounts,
                tiny = tiny,
                onClick = { appModel.enterAdminMode() }
            )
        }

        DrawerSeparator(Modifier.padding(vertical = 8.dp))

        // Language dropdown
        DrawerLanguagePicker()

        Column(Modifier.weight(1.0f)) { }

        BottomSpacer()
    }
}

/** Menu of the administrator mode: dashboard, keraunic map, probes (estimates) and the way back. */
@Composable
private fun DrawerContentAdmin(
    modifier: Modifier,
    currentRoute: RouteParameterTo?,
    tiny: Boolean,
    onMenuItemSelected: (title: String, navigateTo: NavigateTo) -> Unit
) {
    val appModel = LocalApp.current

    Column(
        modifier = modifier
            .fillMaxSize()
            .background(color = AppColor.StrikeSidebar)
            .verticalScroll(rememberScrollState())
    ) {
        TopSpacer()

        if (!tiny) {
            DrawerTitle(text = Res.string.menu_admin_mode_title.localized())
        }

        DrawerMenuEntry(
            text = Res.string.menu_sr_dashboard.localized(),
            currentRoute = currentRoute,
            router = RouterDashboard,
            image = Icons.Filled.Home,
            tiny = tiny,
            onMenuItemSelected = onMenuItemSelected
        )

        DrawerSeparator(Modifier.padding(vertical = 8.dp))

        // The estimates (quotes) list, each estimate showing its probes
        DrawerMenuEntry(
            text = Res.string.menu_admin_estimate_list.localized(),
            currentRoute = currentRoute,
            router = RouterEstimatesAdmin,
            image = Icons.Rounded.FixedGps,
            tiny = tiny,
            onMenuItemSelected = onMenuItemSelected
        )

        DrawerSeparator(Modifier.padding(vertical = 8.dp))

        DrawerMenuEntry(
            text = Res.string.menu_sr_activity.localized(),
            currentRoute = currentRoute,
            router = RouterMapAdmin,
            image = Icons.Rounded.MapLocation,
            tiny = tiny,
            onMenuItemSelected = onMenuItemSelected
        )

        DrawerSeparator(Modifier.padding(vertical = 8.dp))

        // Promotion codes: list, creation / modification, disable / delete
        DrawerMenuEntry(
            text = Res.string.menu_admin_promo_codes.localized(),
            currentRoute = currentRoute,
            router = RouterPromotionCodesAdmin,
            image = Icons.Filled.Star,
            tiny = tiny,
            onMenuItemSelected = onMenuItemSelected
        )

        DrawerSeparator(Modifier.padding(vertical = 8.dp))

        DrawerItem(
            text = Res.string.menu_admin_mode_exit.localized(),
            router = null,
            image = Icons.Outlined.ManageAccounts,
            tiny = tiny,
            onClick = { appModel.exitAdminMode() }
        )

        DrawerSeparator(Modifier.padding(vertical = 8.dp))

        DrawerLanguagePicker()

        Column(Modifier.weight(1.0f)) { }

        BottomSpacer()
    }
}

@Composable
private fun DrawerMenuEntry(
    text: String,
    currentRoute: RouteParameterTo?,
    router: RouterNoParameters<*>,
    image: ImageVector,
    tiny: Boolean,
    onMenuItemSelected: (title: String, navigateTo: NavigateTo) -> Unit
) {
    DrawerItem(
        text = text,
        currentRoute = currentRoute,
        router = router,
        image = image,
        onClick = { onMenuItemSelected(text, router.navigateTo()) },
        tiny = tiny
    )
}

@Composable
private fun DrawerLanguagePicker() {
    val appModel = LocalApp.current
    val state by appModel.states.collectAsState()
    val languages = listOf(
        "🇫🇷" to "fr",
        "🇬🇧" to "en",
        "🇪🇸" to "es",
        "🇧🇷" to "pt"
    )
    val selectedLang = state.appLanguage
    var expanded by remember { mutableStateOf(false) }
    val selectedFlag = languages.first { it.second == selectedLang }.first

    Box(modifier = Modifier.padding(horizontal = 16.dp)) {
        Row(
            modifier = Modifier
                .clickable(
                    indication = null,
                    interactionSource = remember { MutableInteractionSource() }
                ) { expanded = true }
                .padding(vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            TextNormal(
                text = selectedFlag,
                fontSize = 24.sp
            )
            Spacer(modifier = Modifier.width(6.dp))
            Image(
                modifier = Modifier.width(18.dp),
                imageVector = Icons.Filled.ArrowDropDown,
                contentDescription = null,
                colorFilter = ColorFilter.tint(AppColor.White)
            )
        }

        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false }
        ) {
            languages.forEach { (flag, code) ->
                DropdownMenuItem(onClick = {
                    appModel.setLanguage(code)
                    expanded = false
                }) {
                    TextNormal(
                        text = flag,
                        fontSize = 24.sp
                    )
                }
            }
        }
    }
}

@HotPreview(widthDp = 300, heightDp = 900, darkMode = true)
@HotPreview(widthDp = 300, heightDp = 900, darkMode = false)
@Composable
private fun PreviewDrawerContent() {
    HotPreviewApp(isDarkTheme = isSystemInDarkTheme()) {
        DrawerContentNormal(tiny = false) { _, _ -> }
    }
}
